/**
 * Free, no-key ADS-B data providers (readsb JSON format).
 * Tried in order: adsb.lol → adsb.fi → adsb.one → airplanes.live.
 * All are community-run, need no registration, and return the same
 * `{ ac: [...] }` readsb shape — independent infrastructure gives us
 * network redundancy when one host is unreachable (e.g. corporate network).
 */

const FT_TO_M = 0.3048;
const KT_TO_MS = 0.514444;
const FPM_TO_MS = 0.00508;
const KM_TO_NM = 1 / 1.852;

// Always call the ADS-B APIs same-origin via a reverse proxy, so corporate
// proxies that strip CORS headers can't break us:
//   - dev:  the Vite dev-server proxy (see vite.config.js)
//   - prod: an Amplify "Rewrites and redirects" rule (status 200) per host:
//           /adsb-lol/<*>       -> https://api.adsb.lol/<*>
//           /adsb-fi/<*>        -> https://opendata.adsb.fi/<*>
//           /adsb-one/<*>       -> https://api.adsb.one/<*>
//           /airplanes-live/<*> -> https://api.airplanes.live/<*>
const LOL_BASE = '/adsb-lol';
const FI_BASE = '/adsb-fi';
const ONE_BASE = '/adsb-one';
const LIVE_BASE = '/airplanes-live';

export const PROVIDERS = [
  {
    name: 'adsb.lol',
    buildUrl: (lat, lon, distNm) =>
      `${LOL_BASE}/v2/lat/${lat}/lon/${lon}/dist/${distNm}`
  },
  {
    name: 'adsb.fi',
    buildUrl: (lat, lon, distNm) =>
      `${FI_BASE}/api/v2/lat/${lat}/lon/${lon}/dist/${distNm}`
  },
  {
    name: 'adsb.one',
    buildUrl: (lat, lon, distNm) =>
      `${ONE_BASE}/v2/lat/${lat}/lon/${lon}/dist/${distNm}`
  },
  {
    // airplanes.live uses a /point/ path instead of /lat//lon//dist/
    name: 'airplanes.live',
    buildUrl: (lat, lon, distNm) =>
      `${LIVE_BASE}/v2/point/${lat}/${lon}/${distNm}`
  }
];

/**
 * Normalize a readsb aircraft record to the internal flight object shape.
 * Returns null for records without a usable position.
 */
export function normalizeAircraft(ac, nowMs) {
  if (typeof ac.lat !== 'number' || typeof ac.lon !== 'number') {
    return null;
  }

  const onGround = ac.alt_baro === 'ground';

  let altitude = 0;
  if (typeof ac.alt_geom === 'number') {
    altitude = ac.alt_geom * FT_TO_M;
  } else if (typeof ac.alt_baro === 'number') {
    altitude = ac.alt_baro * FT_TO_M;
  }
  if (altitude < 0) altitude = 0;

  const heading =
    (typeof ac.track === 'number' ? ac.track : null) ??
    (typeof ac.true_heading === 'number' ? ac.true_heading : null) ??
    0;

  const velocity = (typeof ac.gs === 'number' ? ac.gs : 0) * KT_TO_MS;

  const rawRate =
    (typeof ac.baro_rate === 'number' ? ac.baro_rate : null) ??
    (typeof ac.geom_rate === 'number' ? ac.geom_rate : null) ??
    0;
  const verticalRate = rawRate * FPM_TO_MS;

  let phase = 'cruising';
  if (onGround) {
    phase = 'ground';
  } else if (verticalRate > 1.5) {
    phase = 'climbing';
  } else if (verticalRate < -1.5) {
    phase = 'descending';
  }

  const callsign = (ac.flight || '').trim() || ac.r || ac.hex;

  return {
    id: ac.hex,
    callsign,
    position: {
      lat: ac.lat,
      lon: ac.lon,
      altitude,
      heading
    },
    velocity,
    verticalRate,
    onGround,
    timestamp: nowMs,
    source: 'adsb',
    registration: ac.r ?? null,
    aircraftType: ac.t ?? null,
    category: ac.category ?? null,
    phase,
    squawk: ac.squawk ?? null
  };
}

/**
 * Fetch and normalize aircraft around a point from a single provider.
 * @param {{name: string, buildUrl: Function}} provider
 * @param {{lat: number, lon: number, radiusKm: number}} area
 * @param {number} timeoutMs
 * @returns {Promise<Array>} normalized flight objects
 */
export async function fetchFromProvider(provider, { lat, lon, radiusKm }, timeoutMs = 8000) {
  const distNm = Math.min(Math.max(Math.round(radiusKm * KM_TO_NM), 1), 250);
  const url = provider.buildUrl(lat.toFixed(4), lon.toFixed(4), distNm);

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  let response;
  try {
    response = await fetch(url, {
      headers: { 'Accept': 'application/json' },
      signal: controller.signal
    });
  } finally {
    clearTimeout(timeoutId);
  }

  if (!response.ok) {
    throw new Error(`${provider.name} responded with ${response.status}`);
  }

  const json = await response.json();
  // adsb.lol uses "ac", adsb.fi uses "aircraft" — same record shape
  const raw = json?.ac ?? json?.aircraft;
  const list = Array.isArray(raw) ? raw : [];
  const now = Date.now();

  const flights = [];
  for (const ac of list) {
    const flight = normalizeAircraft(ac, now);
    if (flight) flights.push(flight);
  }
  return flights;
}
